import { fetchWithAuth } from './apiUtils';
import { getAuthToken } from './authService';
import { API_URL } from '../constants/api';

const API_BASE_URL = `${API_URL}/contacts`;

/**
 * Servicio para gestión de contactos
 */
const contactService = {
  /**
   * Obtener todos los contactos con paginación
   * @param {Object} options - Opciones de consulta
   * @param {number} options.page - Número de página (default: 1)
   * @param {number} options.limit - Elementos por página (default: 10)
   * @param {string} options.search - Término de búsqueda
   * @param {boolean} options.withSubscription - Filtrar por suscripción
   * @returns {Promise} - Promesa con la lista de contactos paginada
   */
  async getAllContacts(options = {}) {
    const token = getAuthToken();
    
    // Construir parámetros de consulta
    const queryParams = new URLSearchParams();
    if (options.page) queryParams.append('page', options.page);
    if (options.limit) queryParams.append('limit', options.limit);
    if (options.search) queryParams.append('search', options.search);
    if (options.withSubscription !== undefined) queryParams.append('withSubscription', options.withSubscription);
    
    const url = queryParams.toString() ? `${API_BASE_URL}?${queryParams}` : API_BASE_URL;
    return await fetchWithAuth(url, {}, token);
  },

  /**
   * Obtener estadísticas de contactos
   * @returns {Promise} - Promesa con las estadísticas
   */
  async getContactStats() {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/stats`, {}, token);
  },

  /**
   * Crear un nuevo contacto
   * @param {Object} contactData - Datos del contacto
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async createContact(contactData) {
    const token = getAuthToken();
    return await fetchWithAuth(API_BASE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(contactData),
    }, token);
  },

  /**
   * Actualizar un contacto
   * @param {string} id - ID del contacto
   * @param {Object} contactData - Datos actualizados del contacto
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async updateContact(id, contactData) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(contactData),
    }, token);
  },

  /**
   * Eliminar un contacto
   * @param {string} id - ID del contacto
   * @returns {Promise} - Promesa con la respuesta del servidor
   */
  async deleteContact(id) {
    const token = getAuthToken();
    return await fetchWithAuth(`${API_BASE_URL}/${id}`, {
      method: 'DELETE',
    }, token);
  },
};

/**
 * Buscar contactos paginados por nombre, celular o email
 * @param {string} search - Término de búsqueda
 * @param {number} page - Número de página (1-based)
 * @param {number} limit - Resultados por página
 * @returns {Promise<{contacts: Array, pagination: Object}>}
 */
export async function searchContactsPaginated(search, page = 1, limit = 10) {
  const token = getAuthToken();
  
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  params.append('page', page);
  params.append('limit', limit);

  const url = `${API_BASE_URL}?${params.toString()}`;
  const response = await fetchWithAuth(url, {}, token);

  if (!response.success) {
    throw new Error(response.message || 'Error buscando contactos');
  }

  // El backend retorna { data: { contacts, pagination }, ... }
  return {
    contacts: response.data.contacts,
    pagination: response.data.pagination
  };
}

export default contactService; 